"use client"
import { Espece } from "@repo/types";
import { ColumnDef } from "@tanstack/react-table";
import { Eye, Pencil, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { DataTable } from "../../components/ui/data-table";
import DataTableSearch from "../../components/ui/data-table/data-table-search";
import { StatusBadge } from "../../components/ui/data-table/status-badge";
import { Label } from "../../components/ui/label";
import { ListSortSelect } from "../../components/ui/list-sort-select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { useDeleteEspece, useRestoreEspece } from "../../hooks/api/especes";
import { EspeceTableImageCell } from "./components/espece-table-image-cell";

interface EspeceListProps {
  especes: Espece[];
  isLoading: boolean;
  error: Error | null;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  page: number;
  limit: number;
  total: number;
  sortBy: string;
  onLimitChange: (limit: number) => void;
  onSearchChange: (search: string) => void;
  searchTerm: string;
  onPageChange: (page: number) => void;
  handleSortChange: (sort: string) => void;
  showDeleted: boolean;
  onShowDeletedChange: (show: boolean) => void;
  pageCount: number;
  mode?: "manage" | "browse";
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatCultivar = (cultivar: string | null | undefined): string => {
  if (!cultivar) return "—";
  return cultivar.charAt(0).toUpperCase() + cultivar.slice(1).toLowerCase();
};

const formatEspeceName = (name: string | null | undefined): string => {
  if (!name) return "—";
  return name.toLowerCase();
};

const ESPECE_SORT_OPTIONS = [
  { value: "name", label: "Nom" },
  { value: "genre", label: "Genre" },
  { value: "recent", label: "Plus récents" },
  { value: "oldest", label: "Plus anciens" },
] as const;

const formatTarif = (tarif: number | null | undefined): string => {
  if (tarif == null) return "—";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(tarif);
};

// ─── Component ────────────────────────────────────────────────────────────────

export function EspeceList({
  especes,
  isLoading,
  error,
  onView,
  onEdit,
  page,
  limit,
  total,
  sortBy,
  onLimitChange,
  onSearchChange,
  searchTerm,
  onPageChange,
  handleSortChange,
  showDeleted,
  onShowDeletedChange,
  pageCount,
  mode = "manage",
}: EspeceListProps) {
  const [especeToDelete, setEspeceToDelete] = useState<number | null>(null);

  const isBrowseMode = mode === "browse";

  const deleteEspeceMutation = useDeleteEspece();
  const restoreEspeceMutation = useRestoreEspece();

  const handleConfirmDelete = () => {
    if (especeToDelete) {
      deleteEspeceMutation.mutate(especeToDelete, {
        onSuccess: () => setEspeceToDelete(null),
        onError: (err) => console.error("Error deleting espece:", err),
      });
    }
  };

  const handleRestoreClick = (id: number) => {
    restoreEspeceMutation.mutate(id, {
      onError: (err) => console.error("Error restoring espece:", err),
    });
  };

  // ─── Columns ──────────────────────────────────────────────────────────────

  const columns: ColumnDef<Espece>[] = [
    {
      id: "img_url",
      header: "Image",
      cell: ({ row }) => <EspeceTableImageCell espece={row.original} />,
    },
    {
      accessorKey: "genre",
      header: "Genre",
      cell: ({ row }) => (
        <span className="font-medium text-sm">{row.original.genre || "—"}</span>
      ),
    },
    {
      accessorKey: "name",
      header: "Espèce",
      cell: ({ row }) => (
        <span className="text-sm text-foreground">
          {formatEspeceName(row.original.name)}
        </span>
      ),
    },
    {
      accessorKey: "cultivar",
      header: "Cultivar",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {formatCultivar(row.original.cultivar)}
        </span>
      ),
    },
    {
      accessorKey: "nom_fr",
      header: "Nom français",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.nom_fr || "—"}
        </span>
      ),
    },
    {
      accessorKey: "categorie",
      header: "Catégorie",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.categorie || "—"}
        </span>
      ),
    },
    {
      accessorKey: "tarif",
      header: "Tarif",
      cell: ({ row }) => (
        <span className="text-sm font-medium text-foreground">
          {formatTarif(row.original.tarif)}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: "Statut",
      cell: ({ row }) => (
        <StatusBadge
          tone={row.original.is_deleted ? "deleted" : "active"}
          label={row.original.is_deleted ? "Supprimé" : "Actif"}
          withIcon
        />
      ),
    },
    ...(!isBrowseMode
      ? [
          {
      id: "actions",
      header: () => <div className="text-right pr-2">Actions</div>,
      cell: ({ row }) => {
        const espece = row.original;
        return (
          <div className="flex justify-end items-center gap-1 pr-2">
            {/* View */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => onView(espece.id)}
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Voir le détail</TooltipContent>
            </Tooltip>

            {/* Edit */}
            {!espece.is_deleted && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => onEdit(espece.id)}
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Modifier</TooltipContent>
              </Tooltip>
            )}

            {/* Delete / Restore */}
            {!espece.is_deleted ? (
              <AlertDialog
                open={especeToDelete === espece.id}
                onOpenChange={(open) =>
                  setEspeceToDelete(open ? espece.id : null)
                }
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                  </TooltipTrigger>
                  <TooltipContent>Supprimer</TooltipContent>
                </Tooltip>

                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer cette espèce ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Cette action est réversible. L'espèce sera marquée comme
                      supprimée et pourra être restaurée ultérieurement.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleConfirmDelete}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Supprimer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0"
                    onClick={() => handleRestoreClick(espece.id)}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Restaurer</TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      },
          } satisfies ColumnDef<Espece>,
        ]
      : []),
  ];

  // ─── Error state ────────────────────────────────────────────────────────────

  if (error)
    return (
      <Alert variant="destructive">
        <AlertDescription>Erreur : {error.message}</AlertDescription>
      </Alert>
    );

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="flex-1 min-w-[200px]">
          <DataTableSearch
            value={searchTerm}
            onChange={onSearchChange}
            placeholder="Rechercher par nom, genre…"
          />
        </div>

        <ListSortSelect
          id="espece-sort"
          value={sortBy}
          options={[...ESPECE_SORT_OPTIONS]}
          onValueChange={handleSortChange}
        />

        {/* Show deleted toggle */}
        {!isBrowseMode && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="showDeletedEspeces"
              checked={showDeleted}
              onCheckedChange={(checked) => onShowDeletedChange(checked === true)}
            />
            <Label
              htmlFor="showDeletedEspeces"
              className="text-sm text-muted-foreground cursor-pointer select-none"
            >
              Afficher supprimées
            </Label>
          </div>
        )}
      </div>

      {/* Table */}
      <DataTable
        serverSidePagination
        columns={columns}
        data={especes}
        isLoading={isLoading}
        renderSearchOutside
        externalGlobalFilter={searchTerm}
        onGlobalFilterChange={onSearchChange}
        pageCount={pageCount}
        pageSize={limit}
        totalCount={total}
        onPageChange={onPageChange}
        onPageSizeChange={onLimitChange}
        searchPlaceholder="Rechercher une espèce…"
        currentPage={page}
        density="compact"
        stickyPagination
      />
    </div>
  );
}
